import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import { 
  Mail, Save, Send, RefreshCw, AlertCircle, Check, Eye, EyeOff,
  Server, Key, Settings
} from 'lucide-react';
import { cn } from '../../lib/utils';

const EMAIL_PROVIDERS = [
  { id: 'default', name: 'Platform Default', description: 'Use Edrano\'s email service' },
  { id: 'smtp', name: 'Custom SMTP', description: 'Connect your own SMTP server' },
  { id: 'maileroo', name: 'Maileroo', description: 'API-based email delivery' },
  { id: 'sendgrid', name: 'SendGrid', description: 'Twilio SendGrid API' },
  { id: 'mailgun', name: 'Mailgun', description: 'Mailgun API' }
];

export default function EmailSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [settings, setSettings] = useState({
    provider: 'default',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    api_key: '',
    from_email: '',
    from_name: '',
    reply_to: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await adminAPI.getEmailSettings();
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Failed to load email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateEmailSettings(settings);
      toast.success('Email settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    setTesting(true);
    try {
      await adminAPI.testEmailSettings({ to_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default email settings?')) return;
    
    try {
      await adminAPI.resetEmailSettings();
      setSettings({
        provider: 'default',
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_use_tls: true,
        api_key: '',
        from_email: '',
        from_name: '',
        reply_to: ''
      });
      toast.success('Settings reset to default');
    } catch (error) {
      toast.error('Failed to reset settings');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A]">Email Settings</h1>
            <p className="text-sm text-[#525252] mt-1">Configure your email provider for sending notifications</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm text-[#525252] hover:bg-[#F5F5F5] rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Reset to Default
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-black text-white text-sm font-medium rounded-lg hover:bg-[#262626] disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin inline mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 inline mr-2" />
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Provider Selection */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Email Provider</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {EMAIL_PROVIDERS.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => setSettings({ ...settings, provider: provider.id })}
                    className={cn(
                      "p-4 rounded-lg border-2 text-left transition-all",
                      settings.provider === provider.id
                        ? "border-orange-500 bg-orange-50"
                        : "border-[#EBEBEB] hover:border-[#D4D4D4]"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        settings.provider === provider.id ? "bg-orange-100" : "bg-[#F5F5F5]"
                      )}>
                        {provider.id === 'default' && <Mail className="w-5 h-5 text-orange-500" />}
                        {provider.id === 'smtp' && <Server className="w-5 h-5 text-blue-500" />}
                        {provider.id !== 'default' && provider.id !== 'smtp' && <Key className="w-5 h-5 text-purple-500" />}
                      </div>
                      <div>
                        <p className="font-medium text-[#0A0A0A]">{provider.name}</p>
                        <p className="text-xs text-[#525252]">{provider.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Provider-specific Settings */}
            {settings.provider === 'smtp' && (
              <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
                <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">SMTP Configuration</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1">SMTP Host</label>
                    <input
                      type="text"
                      value={settings.smtp_host || ''}
                      onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                      placeholder="smtp.example.com"
                      className="w-full px-3 py-2 border border-[#EBEBEB] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1">SMTP Port</label>
                    <input
                      type="number"
                      value={settings.smtp_port || 587}
                      onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                      placeholder="587"
                      className="w-full px-3 py-2 border border-[#EBEBEB] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1">Username</label>
                    <input
                      type="text"
                      value={settings.smtp_username || ''}
                      onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                      placeholder="your@email.com"
                      className="w-full px-3 py-2 border border-[#EBEBEB] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1">Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={settings.smtp_password || ''}
                        onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                        placeholder="••••••••"
                        className="w-full px-3 py-2 pr-10 border border-[#EBEBEB] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#525252]"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.smtp_use_tls}
                        onChange={(e) => setSettings({ ...settings, smtp_use_tls: e.target.checked })}
                        className="w-4 h-4 rounded border-[#EBEBEB] text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm text-[#0A0A0A]">Use TLS encryption</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {['maileroo', 'sendgrid', 'mailgun'].includes(settings.provider) && (
              <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
                <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">API Configuration</h2>
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0A] mb-1">API Key</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={settings.api_key || ''}
                      onChange={(e) => setSettings({ ...settings, api_key: e.target.value })}
                      placeholder="Enter your API key"
                      className="w-full px-3 py-2 pr-10 border border-[#EBEBEB] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#525252]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Sender Settings */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Sender Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0A] mb-1">From Email</label>
                  <input
                    type="email"
                    value={settings.from_email || ''}
                    onChange={(e) => setSettings({ ...settings, from_email: e.target.value })}
                    placeholder="noreply@yourdomain.com"
                    className="w-full px-3 py-2 border border-[#EBEBEB] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0A] mb-1">From Name</label>
                  <input
                    type="text"
                    value={settings.from_name || ''}
                    onChange={(e) => setSettings({ ...settings, from_name: e.target.value })}
                    placeholder="Your Company"
                    className="w-full px-3 py-2 border border-[#EBEBEB] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-[#0A0A0A] mb-1">Reply-To Email</label>
                  <input
                    type="email"
                    value={settings.reply_to || ''}
                    onChange={(e) => setSettings({ ...settings, reply_to: e.target.value })}
                    placeholder="support@yourdomain.com"
                    className="w-full px-3 py-2 border border-[#EBEBEB] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Test Email Sidebar */}
          <div className="space-y-6">
            <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Test Configuration</h2>
              <p className="text-sm text-[#525252] mb-4">
                Send a test email to verify your settings are working correctly.
              </p>
              <div className="space-y-3">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-3 py-2 border border-[#EBEBEB] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                />
                <button
                  onClick={handleTest}
                  disabled={testing || !testEmail}
                  className="w-full px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {testing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Test Email
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Important</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Make sure your email provider is properly configured and verified to avoid emails being marked as spam.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
